/**
 * Favourites / wishlist without an account.
 *
 * The list is product ids in localStorage under 'zimple_wishlist'. Ids rather than handles for
 * two reasons: the storefront search endpoint takes an "id:" query, which lets the favourites
 * page fetch every saved product in one request, and an id survives a product being renamed.
 *
 * Three custom elements share one store and one event:
 *   <wishlist-button data-product-id>  the heart, on cards and on the product page
 *   <wishlist-count>                   the number beside the header icon
 *   <wishlist-list data-url>           the favourites page grid
 *
 * Every mutation goes through write(), which dispatches 'wishlist:change' on document. Each
 * element re-derives its own state from the store on that event, so a heart clicked in one place
 * updates the counter, the other hearts for the same product, and the favourites page - without
 * any element knowing about the others.
 */
(function () {
  'use strict';

  const KEY = 'zimple_wishlist';
  const CHANGED = 'wishlist:change';

  /* The storefront search endpoint paginates, and 50 is its ceiling per page. A wishlist longer
     than this would need a second request; capping keeps the page to one and is far beyond what
     a real list reaches. */
  const FETCH_LIMIT = 50;

  /**
   * Deliberately forgiving. A private window, site data blocked in the browser, or a value
   * corrupted by something else all have to degrade to "no favourites" rather than throw on
   * every page load.
   * @returns {number[]} Saved product ids, oldest first, de-duplicated.
   */
  function read() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map(Number)
        .filter((id, index, all) => Number.isFinite(id) && id > 0 && all.indexOf(id) === index);
    } catch (error) {
      return [];
    }
  }

  /**
   * @param {number[]} ids Ids to persist.
   */
  function write(ids) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(ids));
    } catch (error) {
      /* Quota exceeded, or storage denied. The event still fires below, so the heart responds to
         the click - the choice just will not survive the next page load. Better than a toggle
         that appears broken. */
    }

    document.dispatchEvent(new CustomEvent(CHANGED, { detail: { ids: ids } }));
  }

  const wishlist = {
    key: KEY,
    event: CHANGED,
    ids: read,
    count: function () {
      return read().length;
    },
    has: function (id) {
      return read().indexOf(Number(id)) !== -1;
    },
    add: function (id) {
      const ids = read();
      if (ids.indexOf(Number(id)) === -1) {
        ids.push(Number(id));
        write(ids);
      }
    },
    remove: function (id) {
      const ids = read();
      const at = ids.indexOf(Number(id));
      if (at !== -1) {
        ids.splice(at, 1);
        write(ids);
      }
    },
    /**
     * @param {number|string} id Product id.
     * @returns {boolean} True if the product is now a favourite.
     */
    toggle: function (id) {
      const ids = read();
      const at = ids.indexOf(Number(id));

      if (at === -1) {
        ids.push(Number(id));
      } else {
        ids.splice(at, 1);
      }

      write(ids);
      return at === -1;
    }
  };

  window.ZimpleWishlist = wishlist;

  /* Fires only in other tabs, never in the one that wrote - so there is no loop with the
     CustomEvent in write(). Keeps a second tab's hearts and counter honest. */
  window.addEventListener('storage', function (event) {
    if (event.key === KEY) {
      document.dispatchEvent(new CustomEvent(CHANGED, { detail: { ids: read() } }));
    }
  });

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.onClick = this.onClick || this.handleClick.bind(this);
      this.onChange = this.onChange || this.sync.bind(this);

      this.button.addEventListener('click', this.onClick);
      document.addEventListener(CHANGED, this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      if (this.onChange) document.removeEventListener(CHANGED, this.onChange);
    }

    get productId() {
      return Number(this.dataset.productId);
    }

    handleClick(event) {
      /* Cards put the image inside a link, and some card layouts overlay the whole card with
         one. Neither should fire when the heart is what was clicked. */
      event.preventDefault();
      event.stopPropagation();

      wishlist.toggle(this.productId);
    }

    sync() {
      const active = wishlist.has(this.productId);

      this.classList.toggle('is-active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');

      const label = active ? this.button.dataset.labelRemove : this.button.dataset.labelAdd;
      if (!label) return;

      /* Where the button shows its label, that text is the accessible name - setting aria-label
         as well would override what a screen reader reads. Only the icon-only variant needs it. */
      const visible = this.button.querySelector('[data-wishlist-label]');

      if (visible) {
        visible.textContent = label;
      } else {
        this.button.setAttribute('aria-label', label);
      }
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange || this.sync.bind(this);
      document.addEventListener(CHANGED, this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      if (this.onChange) document.removeEventListener(CHANGED, this.onChange);
    }

    sync() {
      const count = wishlist.count();

      this.hidden = count === 0;
      this.textContent = count > 99 ? '99+' : String(count);

      /* The bubble is decorative; the readable count belongs on the link that owns it, so a
         screen reader announces "Favourites, 3 saved products" rather than a bare number. */
      const link = this.closest('a');
      const template = link && link.dataset.countLabel;
      if (template) {
        const readable = link.querySelector('.wishlist-icon__count-label');
        if (readable) readable.textContent = template.replace('[count]', String(count));
      }
    }
  }

  class WishlistList extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.loadingState = this.querySelector('[data-wishlist-loading]');
      this.errorState = this.querySelector('[data-wishlist-error]');

      this.onChange = this.onChange || this.handleChange.bind(this);
      document.addEventListener(CHANGED, this.onChange);

      this.load();
    }

    disconnectedCallback() {
      if (this.onChange) document.removeEventListener(CHANGED, this.onChange);
    }

    /**
     * A change while the page is open is almost always a removal from this very page, which only
     * needs the card taken out - no request. Anything else (an addition in another tab) falls
     * back to a reload of the grid.
     */
    handleChange() {
      const ids = wishlist.ids();

      if (!this.grid) return;

      const cards = Array.from(this.grid.querySelectorAll('[data-product-id]'));
      const rendered = cards.map((card) => Number(card.dataset.productId));
      const added = ids.filter((id) => rendered.indexOf(id) === -1);

      if (added.length) {
        this.load();
        return;
      }

      cards.forEach((card) => {
        if (ids.indexOf(Number(card.dataset.productId)) === -1) {
          const item = card.closest('li') || card;
          item.remove();
        }
      });

      if (!this.grid.querySelector('[data-product-id]')) this.show('empty');
    }

    /**
     * @param {'grid'|'empty'|'loading'|'error'} state Which of the four to show.
     */
    show(state) {
      if (this.grid) this.grid.hidden = state !== 'grid';
      if (this.emptyState) this.emptyState.hidden = state !== 'empty';
      if (this.loadingState) this.loadingState.hidden = state !== 'loading';
      if (this.errorState) this.errorState.hidden = state !== 'error';
    }

    async load() {
      const ids = wishlist.ids();

      if (!ids.length) {
        this.show('empty');
        return;
      }

      this.show('loading');

      /* One request for the whole list: the search endpoint understands "id:" terms, and this
         section renders search.results when it is fetched that way. Same approach the theme's
         own recently-viewed section uses. */
      const query = ids.slice(-FETCH_LIMIT).map((id) => 'id:' + id).join(' OR ');

      try {
        const response = await fetch(this.dataset.url + '&q=' + encodeURIComponent(query));
        if (!response.ok) throw new Error(String(response.status));

        const template = document.createElement('template');
        template.innerHTML = await response.text();

        const source = template.content.querySelector('[data-wishlist-grid]');
        if (!source || !this.grid) throw new Error('no grid in response');

        this.grid.innerHTML = source.innerHTML;
        this.reorder(ids);

        /* Search only returns products published to the Online Store. Anything saved that has
           since been unpublished or deleted simply is not in the response, so it is dropped from
           storage rather than left to fail on every visit. */
        this.prune(ids);

        this.show(this.grid.querySelector('[data-product-id]') ? 'grid' : 'empty');
      } catch (error) {
        this.show('error');
      }
    }

    /**
     * Search decides its own order. Most recently favourited first is the useful one here.
     * @param {number[]} ids Saved ids, oldest first.
     */
    reorder(ids) {
      ids
        .slice()
        .reverse()
        .forEach((id) => {
          const card = this.grid.querySelector('[data-product-id="' + id + '"]');
          const item = card && (card.closest('li') || card);
          if (item) this.grid.appendChild(item);
        });
    }

    /**
     * @param {number[]} ids Ids that were asked for.
     */
    prune(ids) {
      const found = Array.from(this.grid.querySelectorAll('[data-product-id]')).map((card) =>
        Number(card.dataset.productId)
      );
      const missing = ids.filter((id) => found.indexOf(id) === -1);

      /* Only prune what was actually asked for. Ids beyond FETCH_LIMIT were never requested and
         must not be mistaken for products that no longer exist. */
      const requested = ids.slice(-FETCH_LIMIT);
      const gone = missing.filter((id) => requested.indexOf(id) !== -1);

      if (gone.length) write(ids.filter((id) => gone.indexOf(id) === -1));
    }
  }

  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-list')) customElements.define('wishlist-list', WishlistList);
})();
